/**
 * TEMPLATE-EDITOR.ROUTE
 * Edit questionnaire
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"

import { connect } from "react-redux"
import { TemplateState } from "@/redux/template.types"
import { Session } from "@/redux/_session.types"
import NavFocus from "@/nav/nav.focus"
import PageLoader from "@/components/page-loader"
import { Topic, TopicState } from "@/redux/topic.types"
import { ProjectState } from "@/redux/project.types"
import ReviewMessage, { AiActionButton, ReviewMessageGroup } from "@/widgets/review-message.widget"
import ListButton from "@/components/list-button"
import Button from "@/components/button"
import ProjectReviewLauncher from "@/widgets/project-review-launcher.widget"
import DuplicateTemplateAskModal from "@/modals/duplicate-template-ask.modal"
import DuplicateTemplateModal from "@/modals/duplicate-template.modal"
import { store } from "@/index"
import { topicActivate, topicAdd, topicEditQuestion, topicFetchAndGet, topicUpdateAndSave } from "@/redux/topic.actions"
import { templateEdit, templateEditAndUpdate } from "@/redux/template.actions"
import { getTemplateIssues, TemplateIssuesContext } from "@/redux/template-issues"
import { getIssuesWhileEditing } from "@/utils/issues-baseline.utils"
import { forgetEditorOrigin, getEditorOrigin } from "@/utils/editor-origin.utils"
import { axisFetchAndGet, axisOpen } from "@/redux/axis.actions"
import DeleteTemplateModal from "@/modals/delete-template.modal"
import EditorAxesWidget from "@/widgets/editor-axes.widget"
import {
  faCheckCircle,
  faClone,
  faCog,
  faCopy,
  faDownload,
  faEye,
  faFolderOpen,
  faLanguage,
  faList,
  faBell,
  faPaperPlane,
  faPencilAlt,
  faPlay,
  faPlus,
  faSave,
  faTimes,
  faArrowLeft,
  faSearch,
  faTrash,
  faUndo
} from "@fortawesome/free-solid-svg-icons"
import EditorAdvancedWidget from "@/widgets/editor-advanced.widget"
import { AxisState } from "@/redux/axis.types"
import AddTopicModal from "@/modals/add-topic.modal"
import ExportTemplateModal from "@/modals/export-template.modal"
import TemplatePreviewHelp from "@/help/template-preview.help"
import AddAxisModal from "@/modals/add-axis.modal"
import ModalConfirm from "@/components/modal-confirm"
import Modal from "@/components/modal"
import SearchQuestionModal from "@/modals/search-question.modal"
import testQuickvote from "@/utils/test-quickvote.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import QuestionScoreHelp from "@/help/question-score.help"
import QuestionPrimaryHelp from "@/help/question-primary.help"
import QuestionAxisHelp from "@/help/question-axis.help"
import QuestionNameHelp from "@/help/question-name.help"
import QuestionReverseHelp from "@/help/question-reverse.help"
import QuestionSpecialHelp from "@/help/question-special.help"
import QuestionTopicHelp from "@/help/question-topic.help"
import QuestionTriggerHelp from "@/help/question-trigger.help"
import QuestionTypeHelp from "@/help/question-type.help"
import QuestionFullscreenHelp from "@/help/question-fullscreen.help"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import { DashboardFilters } from "@/redux/filter.types"
import LoadingModal from "@/modals/loading.modal"
import { projectActivate, projectFetchAll } from "@/redux/project.actions"
import { STATUS_LOADING, STATUS_SAVED } from "@/redux/_status.types"
import {
  fetchQuestionPolarity,
  QuestionPolarityResult
} from "@/features/survey/entities/question-polarity/model/questionPolarity.thunk"
import { isReviewAiEnabled } from "@/utils/review-ai.utils"
import { normalizeText } from "@/utils/reverse-suspect.utils"
import ProjectAddModal from "@/modals/project-add.modal"
import PagePlaceholder from "@/components/page-placeholder"
import { toast } from "react-toastify"
import ActionsMenu from "@/components/actions-menu"
import ContextMenu from "@/components/context-menu"
import ListItem from "@/components/list-item"
import TemplateIssuesList from "@/widgets/template-issues-list.widget"
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import Chip from "@/components/chip"
import getEmoji from "@/utils/get-emoji.utils"
import languagesList from "@/translate/lang.json"
import Space from "@/components/space"
import TemplateGeneratorWidget from "@/widgets/template-generator.widget"

interface Props extends WithTranslation {
  _session: Session
  axis: AxisState
  template: TemplateState
  topic: TopicState
  project: ProjectState
}

//Activate modal if user click on back with unsaved changes
const PREVENT_LEAVING_UNSAVED: boolean = true

//List of available actions for menu-top
const ACTION_ADD: string = "ACTION_ADD"
const ACTION_ADVANCED_MODE: string = "ACTION_ADVANCED_MODE"
const ACTION_CANCEL: string = "ACTION_CANCEL"
const ACTION_CREATE: string = "ACTION_CREATE"
const ACTION_DELETE: string = "ACTION_DELETE"
const ACTION_DUPLICATE: string = "ACTION_DUPLICATE"
const ACTION_EXPORT: string = "ACTION_EXPORT"
const ACTION_ISSUES: string = "ACTION_ISSUES"
const ACTION_LAUNCH: string = "ACTION_LAUNCH"
const ACTION_PARAMS: string = "ACTION_PARAMS"
const ACTION_PREVIEW: string = "ACTION_PREVIEW"
const ACTION_REACH: string = "ACTION_REACH"
const ACTION_SAVE: string = "ACTION_SAVE"
const ACTION_SEARCH: string = "ACTION_SEARCH"
const ACTION_TEST: string = "ACTION_TEST"

//Tutorials
const HELP_AXIS: string = "HELP_AXIS"
const HELP_FULLSCREEN: string = "HELP_FULLSCREEN"
const HELP_PREVIEW: string = "HELP_PREVIEW"
const HELP_PRIMARY: string = "HELP_PRIMARY"
const HELP_TOPIC: string = "HELP_TOPIC"
const HELP_QUESTION: string = "HELP_QUESTION"
const HELP_TYPE: string = "HELP_TYPE"
const HELP_REVERSE: string = "HELP_REVERSE"
const HELP_SCORE: string = "HELP_SCORE"
const HELP_SPECIAL: string = "HELP_RPS"
const HELP_TRIGGER: string = "HELP_TRIGGER"

//List of modals
const MODAL_ADD: string = "MODAL_ADD"
const MODAL_ADD_AXIS: string = "MODAL_ADD_AXIS"
const MODAL_ADD_PROJECT: string = "MODAL_ADD_PROJECT"
const MODAL_DELETE: string = "MODAL_DELETE"
const MODAL_DUPLICATE: string = "MODAL_DUPLICATE"
const MODAL_DUPLICATE_ASK: string = "MODAL_DUPLICATE_ASK"
const MODAL_DUPLICATE_SUCCESS: string = "MODAL_DUPLICATE_SUCCESS"
const MODAL_EXPORT: string = "MODAL_EXPORT"
const MODAL_LEAVING_UNSAVED: string = "MODAL_LEAVE_UNSAVED"
const MODAL_LOADING: string = "MODAL_LOADING"
const MODAL_SEARCH: string = "MODAL_SEARCH"
const MODAL_TEST: string = "MODAL_TEST"

function TemplateEditorRoute(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()
  const location = useLocation()

  //Show widget in list mode
  const [advancedMode, setAdvancedMode] = useState(true)

  //Display current modal in the screen
  const [currentModal, setCurrentModal] = useState<null | string>(null)

  //Display left panel
  const [currentHelp, setCurrentHelp] = useState<null | string>(null)

  //Detect if a project is launch
  const [isProjectLaunch, setIsProjectLaunch] = useState(false)
  //Where the editor was opened from (the project) : the state of the navigation, or the memory of the tab after a refresh
  const [origin] = useState<string | null>(() => location.state?.from ?? getEditorOrigin())
  //Left by the navigation of the app (not by a refresh of the page) : the next visit starts again from its own origin
  useEffect(() => () => forgetEditorOrigin(), [])
  //Answers of the AI just received about the "reverse" option of the questions (the server also remembers them in the options of the questions)
  const [aiMemories, setAiMemories] = useState<{ [questionId: string]: any }>({})

  //Display context menu if ProjectLaunch
  const [showLaunchMenu, setShowLaunchMenu] = useState<boolean>(false)
  const [showIssuesMenu, setShowIssuesMenu] = useState<boolean>(false)
  const wasOwlMenuOpenRef = useRef<boolean>(false) //Open when the owl is pressed : a click outside has closed it before the click of the owl is read
  const [showOwlMenu, setShowOwlMenu] = useState<boolean>(false) //Owl of the project : the points of the questionnaire, save and back to the project

  //Set interface for uploading document (supervisors only)
  const [uploadDocument, setUploadDocument] = useState<boolean>(false)

  //Replacement for <Prompt/>
  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (props.topic.isEdited) {
        //'You have unsaved changes. Are you sure you want to leave?';
        const message: string = t("utils_ask_save_edits")
        event.returnValue = message // Standard for most browsers
        return message // For some older browsers
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [props.topic.isEdited])

  //Topic to open when arriving from an error of a project (?topic=<id>)
  const pendingTopicId = useRef<string | null>(null)
  const pendingLanguage = useRef<string | null>(null)

  //Load page
  //Replace dashboard => for the preview
  //Load axis to display on the right panel
  const loadData = useCallback(async () => {
    store.dispatch(filterReplaceDashboard(new DashboardFilters()))
    store.dispatch(axisFetchAndGet(props.template.active.axesOrder, null))

    //Get URL params to detect if template is a duplicate
    const searchParams = new URLSearchParams(window.location.search)
    const duplicate = searchParams.get("duplicate")
    pendingTopicId.current = searchParams.get("topic")
    pendingLanguage.current = searchParams.get("language")
    window.history.pushState({}, "", window.location.pathname)

    if (duplicate) {
      setCurrentModal(MODAL_DUPLICATE_SUCCESS)
    }

    //Load projects
    const projectResponse: any = props._session.interfaceType === "ADMIN" ? await store.dispatch(projectFetchAll()) : []
    if (!projectResponse.error) {
      //Several projects can use the same template : the project the user comes from wins, so the alerts of two projects are never mixed
      const ofTemplate = projectResponse.filter((x) => x.TemplateId === props.template.active.id)
      const cameFrom: string | undefined = origin ? store.getState().project.active?.id : undefined
      const project = ofTemplate.find((x) => x.id === cameFrom) || ofTemplate.find((x) => x.status === "draft") || ofTemplate[0]
      if (project) {
        store.dispatch(projectActivate(project))
        setIsProjectLaunch(true)
      }
    }
  }, [props._session.interfaceType, props.template.active.axesOrder, props.template.active.id])

  //On load
  useEffect(() => {
    loadData()
  }, [loadData])

  //Open the topic requested by the URL once the topics are loaded
  useEffect(() => {
    if (pendingTopicId.current && props.topic.list.length > 0) {
      const topic = props.topic.list.find((x) => x.id === pendingTopicId.current)
      pendingTopicId.current = null
      if (pendingLanguage.current && props.template.active.languages.includes(pendingLanguage.current)) {
        store.dispatch(templateEdit("language", pendingLanguage.current))
      }
      pendingLanguage.current = null
      if (topic) {
        store.dispatch(topicActivate(topic))
        setTimeout(() => document.querySelector("#topic-" + topic.id)?.scrollIntoView({ behavior: "smooth" }), 200)
      }
    }
  }, [props.topic.list.length])

  //Add a new question
  //If advanced mode
  //> set axis as the same of the current topic displayed
  //> else show modal to pick one the axis if there are more than 2 axes
  function addTopic() {
    if (!isTemplateLock()) {
      if (advancedMode || props.axis.list.length < 1) {
        store.dispatch(
          topicAdd(
            props._session.interfaceType === "SUPERVISOR",
            props.topic.active.Axis,
            !advancedMode
          )
        )
      } else if (props.axis.list.filter((x) => x.open).length === 1) {
        store.dispatch(
          topicAdd(
            props._session.interfaceType === "SUPERVISOR",
            props.axis.list.filter((x) => x.open)[0],
            !advancedMode
          )
        )
      } else {
        setCurrentModal(MODAL_ADD)
      }
    }
  }

  //Previous page
  function back() {
    //Arrived from a project : go back to the project
    if (origin) return navigate(origin)

    navigate(props._session.interfaceType === "SUPERVISOR" ? "/supervisor/templates" : "/survey/templates")
  }

  //Click on one the button at the top
  function clickAction(action) {
    switch (action) {
      case ACTION_ADD:
        return addTopic()
      case ACTION_ADVANCED_MODE:
        return toogleMode()
      case ACTION_CANCEL:
        return store.dispatch(topicFetchAndGet(null))
      case ACTION_CREATE:
        return setCurrentModal(MODAL_ADD_PROJECT)
      case ACTION_DELETE:
        return setCurrentModal(MODAL_DELETE)
      case ACTION_DUPLICATE:
        return setCurrentModal(MODAL_DUPLICATE)
      case ACTION_ISSUES:
        return setShowIssuesMenu(!showIssuesMenu)
      case ACTION_LAUNCH:
        if (props._session.interfaceType === "ADMIN_PARENT") {
          return navigate("/project/list")
        } else if (props._session.interfaceType === "SUPERVISOR") {
          return toast(t("survey_draft_superadmin_title"), { type: "error" })
        } else {
          return createProject()
        }
      case ACTION_PARAMS:
        return toParameters()
      case ACTION_PREVIEW:
        return setCurrentHelp(HELP_PREVIEW)
      case ACTION_REACH:
        return navigate("/project/edit")
      case ACTION_SAVE:
        return save()
      case ACTION_SEARCH:
        return setCurrentModal(MODAL_SEARCH)
      case ACTION_TEST:
        return test()
      case ACTION_EXPORT:
        return setCurrentModal(MODAL_EXPORT)
      default:
        return alert(action + " : à venir")
    }
  }

  //Check if template is editable
  //If template of idtree and connected as customer, ask for duplicate
  function isTemplateLock() {
    if (props.template.active.isLocked(props._session.interfaceType)) {
      setCurrentModal(MODAL_DUPLICATE_ASK)
      return true
    } else {
      return false
    }
  }

  //Launch project from template
  //Detect if a project exist with the same template id
  async function createProject() {
    if (!isProjectLaunch) {
      setCurrentModal(MODAL_ADD_PROJECT)
    } else {
      setShowLaunchMenu(true)
    }
  }

  //Save edits
  async function save() {
    if (!isTemplateLock()) {
      const updatedTopics = props.topic.list.filter((x) => x.updated)
      await store.dispatch(topicUpdateAndSave())

      //Non blocking : the template can be saved, but the questionnaire stays invalid until these points are fixed
      const countErrors = getTemplateIssues(
        props.topic.list,
        props.template.active.options,
        props.template.active.languages,
        { axes: props.axis.list }
      ).filter((issue) => issue.severity === "error").length
      if (countErrors > 0) {
        toast(t("template_issues_saved", { count: countErrors }), { type: "warning" })
      }

      //The AI looks at the questions that were edited (never blocks the save, says nothing if it fails)
      if (store.getState().template.status === STATUS_SAVED) analyzeReverse(updatedTopics)
    }
  }

  //The suggestions of the AI are offered : module "project assistant" of the account, not turned off by the person (not for the supervisor)
  const isAiEnabled: boolean =
    props._session.interfaceType !== "SUPERVISOR" && isReviewAiEnabled(props._session.modules.survey.projectAssistant)

  //Ask the server for the sense of the texts (it answers from its memory when the text did not change, so it costs nothing twice)
  async function analyzeReverse(topics: Topic[]) {
    if (!isAiEnabled) return
    const language: string = props.template.active.languages[0]
    const questions = topics
      .flatMap((topic) => topic.Questions)
      .filter(
        (question) => question.type === "scale" && !question.draft && question.withScore && question.name?.[language]
      )
      .map((question) => ({ id: question.id, text: question.name[language] as string }))
    if (questions.length === 0) return

    try {
      const results: QuestionPolarityResult[] = await store
        .dispatch(fetchQuestionPolarity({ questions, language }))
        .unwrap()
      if (results.length === 0) return
      setAiMemories((previous) => {
        const next = { ...previous }
        results.forEach((result) => {
          const question = questions.find((x) => x.id === result.id)
          if (question) {
            next[result.id] = {
              text: normalizeText(question.text),
              polarity: result.polarity,
              confidence: result.confidence
            }
    }
        })
        return next
      })
    } catch {
      //No suggestion then
    }
  }

  //Apply the suggestion of the AI : the option "reverse" of the question (the question must belong to the active topic)
  function applyReverse(issue: { topicId: string | null; questionId?: string; suggestedReverse?: boolean }) {
    const topic = props.topic.list.find((x) => x.id === issue.topicId)
    if (isTemplateLock() || !topic || !issue.questionId) return
    store.dispatch(topicActivate(topic))
    store.dispatch(topicEditQuestion(issue.questionId, "reverse", !!issue.suggestedReverse))
  }

  //Open quickvote
  //Confirmation if changes not saved
  function test() {
    if (props.topic.isEdited) {
      setCurrentModal(MODAL_TEST)
    } else {
      testConfirm()
    }
  }

  //Open quickvote link
  //Special link for supervisor (to get right route in qv app)
  function testConfirm() {
    testQuickvote(props.template.active.language)
  }

  //Change mode and open axes
  async function toogleMode() {
    setAdvancedMode(!advancedMode)

    if (advancedMode) {
      props.axis.list.forEach((axis) => {
        if (axis.id === props.topic.active.Axis?.id && !axis.open) {
          store.dispatch(axisOpen(axis.id))
        } else if (axis.id !== props.topic.active.Axis?.id && axis.open) {
          store.dispatch(axisOpen(axis.id))
        }
      })

      setTimeout(() => {
        document.querySelector("#topic-" + props.topic.active.id)?.scrollIntoView({
          behavior: "smooth"
        })
      }, 200)
    }
  }

  //Problems found in the questionnaire, each one leads to its topic.
  //While the user has unsaved changes : the problems already there stay (they vanish when fixed), the new ones wait for the save
  const dismissedIssues: string[] = props.template.active.options.dismissedIssues || []
  const rpsEnabled: boolean = props._session.modules.survey.rps || props._session.interfaceType === "SUPERVISOR"
  const currentIssues = useMemo(
    () =>
      getTemplateIssues(props.topic.list, props.template.active.options, props.template.active.languages, {
        axes: props.axis.list,
        rps: rpsEnabled,
        ai: isAiEnabled,
        aiMemories,
        isSupervisor: props._session.interfaceType === "SUPERVISOR",
        isLocked: props.template.active.isLocked(props._session.interfaceType)
      }),
    [
      props.topic.list,
      props.axis.list,
      props.template.active.options,
      props.template.active.languages,
      rpsEnabled,
      isAiEnabled,
      aiMemories
    ]
  )
  const baselineIssueIds = useRef<string[]>([])
  useEffect(() => {
    if (!props.topic.isEdited) baselineIssueIds.current = currentIssues.map((issue) => issue.id)
  })
  const visibleIssues = getIssuesWhileEditing(
    currentIssues.filter((issue) => issue.severity === "error" || !dismissedIssues.includes(issue.id)),
    baselineIssueIds.current,
    props.topic.isEdited,
    (id) => id.startsWith("template:scale") //Edited in the parameters, saved right away
  )
  const hasErrors = visibleIssues.some((issue) => issue.severity === "error")

  //Ignore a warning (saved in the options of the template, critical issues cannot be ignored)
  function dismissIssue(issueId: string) {
    if (!isTemplateLock()) {
      store.dispatch(
        templateEditAndUpdate("options", {
          ...props.template.active.options,
          dismissedIssues: [...dismissedIssues, issueId]
        })
      )
    }
  }

  //Show again every warning ignored
  function restoreIssues() {
    if (!isTemplateLock()) {
      store.dispatch(templateEditAndUpdate("options", { ...props.template.active.options, dismissedIssues: [] }))
    }
  }

  //Bring an element in view and make it pulse, so the point clicked in the owl is found at once
  function pulseElement(elementId: string) {
    const element = document.getElementById(elementId)
    if (!element) return
    element.scrollIntoView({ behavior: "smooth", block: "center" })
    //Same pulses as the alerts of the project (the animation stops by itself)
    setTimeout(() => {
      element.classList.add("hint-pulse-field")
      //A field to fill : ready to type
      const field = element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement ? element : element.querySelector("input, textarea")
      if (field instanceof HTMLElement) field.focus({ preventScroll: true })
      setTimeout(() => element.classList.remove("hint-pulse-field"), 3000)
    }, 350)
  }

  //Go to the topic of a notification
  function goToIssue(issueId: string) {
    setShowOwlMenu(false)
    if (issueId.startsWith("template:scale")) {
      setShowIssuesMenu(false)
      //The parameters open on the scale concerned (template:scale-<count>-<language> or template:scale-missing-<count>-<language>)
      const scale: string | undefined = issueId.match(/^template:scale-(?:missing-)?(\d+)-/)?.[1]
      try {
        if (scale) sessionStorage.setItem("idt-highlight-scale", scale)
      } catch {
        //No highlight then
      }
      return toParameters()
    }
    //A problem of an axis (no name, no question) : the axis is shown, not a question
    const axisId: string | undefined = issueId.match(/^template:axis-(?:empty-)?(.+)$/)?.[1]
    if (axisId) {
      setShowIssuesMenu(false)
      return pulseElement(document.getElementById("axis-" + axisId) ? "axis-" + axisId : "nav-axis-" + axisId)
    }
    //Missing translations : open the first topic to translate, in the language concerned
    const target = visibleIssues.find((issue) => issue.id === issueId)
    if (target && target.language && props.template.active.languages.includes(target.language)) {
      store.dispatch(templateEdit("language", target.language))
    }
    if (issueId.startsWith("template:") && !target?.topicId) {
      return setShowIssuesMenu(false)
    }
    const topic = props.topic.list.find((x) => x.id === (target?.topicId ?? issueId.split(":")[0]))
    setShowIssuesMenu(false)
    if (topic) {
      store.dispatch(topicActivate(topic))
      //The region concerned : the name of the topic, the answers of a question, the question, else the topic (the first place found is shown)
      const [, questionId, part] = issueId.split(":")
      const ofAnswers: boolean = !!part && (part === "empty" || part === "too-few" || (part.startsWith("duplicate-") && !part.startsWith("duplicate-question")))
      setTimeout(() => {
        const candidates: string[] = [
          questionId === "name" ? "topic-name-" + topic.id : "",
          questionId && ofAnswers ? "question-propositions-" + questionId : "",
          questionId && part === "text" ? "question-name-" + questionId : "",
          questionId ? "question-" + questionId : "",
          "topic-" + topic.id,
          topic.questionPrimaryId ? "question-" + topic.questionPrimaryId : ""
        ]
        const found: string | undefined = candidates.find((id) => id && document.getElementById(id))
        if (found) pulseElement(found)
      }, 300)
    }
  }

  //open params
  function toParameters() {
    if (!isTemplateLock()) {
      navigate("/survey/template")
    }
  }

  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const languageLabel = (lg: string) => (getEmoji(lg) !== lg.toUpperCase() ? getEmoji(lg) + " " : "") + lg.toUpperCase()
  //Name in full, in the language of the application ("Français (FR)" in French, "French (FR)" in English)
  const languageFullLabel = (lg: string) => {
    let name: string | undefined
    try {
      name = new Intl.DisplayNames([props._session.language || "fr"], { type: "language" }).of(lg)
    } catch {
      name = undefined
    }
    //No name known by the browser : the one of the list of the languages
    if (!name || name.toLowerCase() === lg.toLowerCase()) {
      const language = languagesList.find((x: any) => x.code === lg)
      name = language ? language.name : lg.toUpperCase()
    }
    return (getEmoji(lg) !== lg.toUpperCase() ? getEmoji(lg) + " " : "") + name.charAt(0).toUpperCase() + name.slice(1) + " (" + lg.toUpperCase() + ")"
  }

  //Language of the editor : a menu on the right of the bar, opened by hovering it, to switch language
  //or to start the automatic translation (module of the account)
  function renderLanguageMenu() {
    const languages = props.template.active.languages
    return (
      <div
        className="rel"
        style={{ margin: "0 8px" }}
        onMouseEnter={() => setShowLanguageMenu(true)}
        onMouseLeave={() => setShowLanguageMenu(false)}>
        <Chip onClick={() => setShowLanguageMenu(!showLanguageMenu)}>{languageLabel(props.template.active.language)} ▾</Chip>
        {showLanguageMenu && (
          <>
          {/** invisible bridge over the gap between the button and the menu : the menu stays open while the mouse crosses it */}
          <div className="abs" style={{ top: "100%", left: 0, right: 0, height: 12 }} />
          <ContextMenu className="!mt-2" positionRight disableAutoClose onClose={() => setShowLanguageMenu(false)}>
            {languages.map((lg) => (
              <ListItem
                key={lg}
                isSmall
                isEditable
                isActive={lg === props.template.active.language}
                onClick={() => {
                  setShowLanguageMenu(false)
                  store.dispatch(templateEdit("language", lg))
                }}>
                {languageFullLabel(lg)}
              </ListItem>
            ))}
            {props._session.isTranslationAllowed && (
              <ListItem
                isSmall
                isEditable
                onClick={() => navigate("/survey/template", { state: { openTranslation: true } })}>
                <FontAwesomeIcon icon={faLanguage} style={{ marginRight: 8, width: 18 }} />
                {t("auto_translate")}
              </ListItem>
            )}
          </ContextMenu>
          </>
        )}
      </div>
    )
  }
  //Not for a locked template (root template) : it is not translated, and isTemplateLock() must not be called while rendering
  const isLanguageMenuDisplayed = props.template.active.languages.length > 1 && !props.template.active.isLocked(props._session.interfaceType)
  const languageAction = isLanguageMenuDisplayed ? [{ id: "ACTION_LANGUAGES", node: renderLanguageMenu() }] : []

  //In a project the owl replaces the bell : same points, same count
  //What is wrong when several places have the same problem : in the plural ("Ces questions n'ont pas encore de texte")
  function getGroupText(issue: { kind: string; description: string }): string {
    const key: string = "template_issue_group_" + issue.kind.replace(/-/g, "_")
    return props.i18n.exists(key) ? t(key) : issue.description
  }

  //The points of the owl : the errors that say the same thing are together (one card, a link for each place), a warning stays alone (it can be ignored)
  function getOwlCards(): { key: string; issues: typeof visibleIssues }[] {
    const cards: { key: string; issues: typeof visibleIssues }[] = []
    visibleIssues.forEach((issue) => {
      const key: string = issue.severity === "error" ? issue.kind + "|" + issue.description : issue.id
      const card = cards.find((x) => x.key === key)
      if (card) card.issues.push(issue)
      else cards.push({ key, issues: [issue] })
    })
    return cards
  }

  const cameFromProject: boolean = !!origin //Opened from the project : the owl offers the way back (not from the list of the questionnaires)
  const isOwlShown: boolean = isProjectLaunch && props.project.active?.status === "draft"
  const issuesErrorsCount: number = visibleIssues.filter((issue) => issue.severity === "error").length

  return (
    <TemplateIssuesContext.Provider value={visibleIssues}>
      <NavFocus
        isCustomText
        isNotScrollable={advancedMode}
        isFullWidth
        onBack={props.topic.isEdited && PREVENT_LEAVING_UNSAVED ? () => setCurrentModal(MODAL_LEAVING_UNSAVED) : back}
        text={t("template_title", {
          name: props.template.active.name[props.template.active.language]
        })}
        status={props.template.status}
        onAction={clickAction}
        actions={
          props.topic.status === STATUS_LOADING
            ? []
            : /** show alternative menu if template supervisor in client interface */
              props.template.active.isLocked(props._session.interfaceType)
              ? [
                  { id: ACTION_TEST, label: t("template_test"), icon: faPlay },
                  { id: ACTION_DUPLICATE, label: t("utils_edit"), icon: faPencilAlt, isLarge: true },
                  {
                    id: ACTION_ADVANCED_MODE,
                    icon: advancedMode ? faList : faClone,
                    label: advancedMode ? t("template_set_display_list") : t("template_set_display_cards")
                  },
                  { id: ACTION_PREVIEW, label: t("dashboard_preview"), icon: faEye },
                  { id: ACTION_EXPORT, label: t("utils_export"), icon: faDownload },
                  { id: ACTION_SEARCH, label: t("question_search"), icon: faSearch },
                  ...languageAction,
                  { id: ACTION_LAUNCH, icon: faPaperPlane, label: t("utils_send"), className: "primary", isLarge: true }
                ]
              : [
                  {
                    id: ACTION_SAVE,
                    label: t("utils_save"),
                    icon: faSave,
                    className: props.topic.isEdited ? "secondary" : "light",
                    isLarge: true
                  },
                  {
                    id: ACTION_TEST,
                    label: t("template_test"),
                    className: !props.topic.isEdited && props.topic.list.length > 0 ? "secondary" : "",
                    icon: faPlay
                  },
                  {
                    id: ACTION_ADVANCED_MODE,
                    icon: advancedMode ? faList : faClone,
                    label: advancedMode ? t("template_set_display_list") : t("template_set_display_cards")
                  },
                  {
                    id: ACTION_CANCEL,
                    label: t("utils_cancel"),
                    icon: faUndo,
                    className: !props.topic.isEdited ? "light" : ""
                  },
                  ...(!isOwlShown && (visibleIssues.length > 0 || dismissedIssues.length > 0)
                    ? [
                        {
                          id: ACTION_ISSUES,
                          label: t("template_issues"),
                          icon: faBell,
                          count: visibleIssues.length,
                          countColor: (hasErrors ? "red" : "orange") as "red" | "orange"
                        }
                      ]
                    : []),
                  { id: ACTION_PARAMS, label: t("utils_params"), icon: faCog },
                  { id: ACTION_PREVIEW, label: t("dashboard_preview"), icon: faEye },
                  { id: ACTION_LAUNCH, icon: faPaperPlane, label: t("utils_send") },
                  { id: ACTION_DUPLICATE, label: t("utils_duplicate"), icon: faCopy },
                  { id: ACTION_EXPORT, label: t("utils_export"), icon: faDownload },
                  { id: ACTION_SEARCH, label: t("question_search"), icon: faSearch },
                  ...languageAction,
                  { id: ACTION_DELETE, label: t("utils_delete"), icon: faTrash },
                  { id: ACTION_ADD, icon: faPlus, label: t("question_add"), isLarge: true, className: "primary" }
                ]
        }>
        {showIssuesMenu && (
          <div style={{ right: "20px", zIndex: 2, top: "50px", position: "fixed" }}>
            <ContextMenu
              className="!p-0"
              positionRight
              width={480}
              maxHeight={400}
              onClose={() => setShowIssuesMenu(false)}>
              <TemplateIssuesList
                groupByKind
                issues={visibleIssues}
                onSelect={goToIssue}
                onDismiss={dismissIssue}
                renderAction={(issueId) => {
                  const issue = visibleIssues.find((x) => x.id === issueId)
                  if (issue?.kind !== "reverse-suspect" || issue.needsConfirmation) return null
                  return (
                    <AiActionButton
                      text={t(issue.suggestedReverse ? "template_issue_reverse_apply" : "template_issue_reverse_unapply")}
                      onClick={() => applyReverse(issue)}
                    />
                  )
                }}
              />
              {dismissedIssues.length > 0 && (
                <ListItem
                  isEditable
                  isSmall
                  onClick={() => {
                    setShowIssuesMenu(false)
                    restoreIssues()
                  }}>
                  <FontAwesomeIcon icon={faUndo} style={{ marginRight: 8, width: 18 }} />
                  {t("template_issues_restore" + (dismissedIssues.length > 1 ? "_multiple" : ""), {
                    count: dismissedIssues.length
                  })}
                </ListItem>
              )}
            </ContextMenu>
          </div>
        )}

        {showLaunchMenu && (
          <div
            style={{
              right: "20px",
              zIndex: 2,
              top: "50px",
              position: "fixed"
            }}>
            <ActionsMenu
              positionRight
              onClose={() => setShowLaunchMenu(false)}
              onSelect={clickAction}
              actions={[
                { icon: faFolderOpen, id: ACTION_REACH, label: t("project_get") },
                {
                  icon: faPlus,
                  id: ACTION_CREATE,
                  label: t("project_add")
                }
              ]}
            />
          </div>
        )}

        {currentModal === MODAL_LEAVING_UNSAVED && (
          <ModalConfirm
            onNo={() => setCurrentModal(null)}
            onYes={back}
            text={t("utils_ask_save")}
            textBold={t("utils_next_ask")}
          />
        )}

        {currentModal === MODAL_LOADING && <LoadingModal />}

        {currentHelp === HELP_FULLSCREEN && <QuestionFullscreenHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_AXIS && <QuestionAxisHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_PRIMARY && <QuestionPrimaryHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_QUESTION && <QuestionNameHelp onClose={() => setCurrentHelp(currentHelp)} />}

        {currentHelp === HELP_REVERSE && <QuestionReverseHelp onClose={() => setCurrentHelp(currentHelp)} />}

        {currentHelp === HELP_SPECIAL && <QuestionSpecialHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_SCORE && <QuestionScoreHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_TOPIC && <QuestionTopicHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_TRIGGER && <QuestionTriggerHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_TYPE && <QuestionTypeHelp onClose={() => setCurrentHelp(null)} />}

        {currentHelp === HELP_PREVIEW && <TemplatePreviewHelp onClose={() => setCurrentHelp(null)} />}

        {currentModal === MODAL_ADD && (
          <AddTopicModal onAddAxis={() => setCurrentModal(MODAL_ADD_AXIS)} onClose={() => setCurrentModal(null)} />
        )}

        {currentModal === MODAL_ADD_AXIS && <AddAxisModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_ADD_PROJECT && <ProjectAddModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_DELETE && <DeleteTemplateModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_DUPLICATE_ASK && <DuplicateTemplateAskModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_DUPLICATE && <DuplicateTemplateModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_DUPLICATE_SUCCESS && (
          <Modal onClose={() => setCurrentModal(null)} isCloseButtonVisible>
            <div className="flex">
              <FontAwesomeIcon
                style={{
                  fontSize: "90px",
                  color: props._session.accountColors.active,
                  marginRight: "30px"
                }}
                icon={faCheckCircle}
              />
              <div>
                <p>
                  <b>{t("utils_congratulations")}</b>
                </p>
                <p>
                  {t("template_duplicate_success", {
                    name: props.template.active.name[props._session.language]
                  })}
                </p>
              </div>
            </div>
          </Modal>
        )}

        {currentModal === MODAL_EXPORT && <ExportTemplateModal onClose={() => setCurrentModal(null)} />}

        {currentModal === MODAL_SEARCH && (
          <SearchQuestionModal language={props.template.active.language} onClose={() => setCurrentModal(null)} />
        )}

        {currentModal === MODAL_TEST && (
          <ModalConfirm onNo={() => setCurrentModal(null)} onYes={testConfirm} textBold={t("utils_ask_save_edits")} />
        )}

        <PageLoader status={props.topic.status}>
          {props.topic.list.length > 0 ? (
            <div>
              {advancedMode ? (
                <EditorAdvancedWidget onHelp={(help) => setCurrentHelp(help)} />
              ) : (
                <EditorAxesWidget
                  onHelp={(help) => setCurrentHelp(help)}
                  onOpenAdvanced={() => setAdvancedMode(true)}
                />
              )}
            </div>
          ) : uploadDocument && props._session.connectedAsSupervisor ? (
            <>
              <u className="_hover grey-t" onClick={() => setUploadDocument(false)}>
                {t("utils_back")}
              </u>
              <TemplateGeneratorWidget currentTemplateId={props.template.active.id} />
            </>
          ) : (
            <>
              <PagePlaceholder
                image="edit"
                title={t("template_questions_empty")}
                button={t("question_add")}
                onClick={() => addTopic()}
              />

              {props._session.connectedAsSupervisor && props._session.supervisorLevel === 3 && (
                <div className="flex">
                  <Space />
                  <u className="_hover grey-t" onClick={() => setUploadDocument(true)}>
                    Uploader un fichier (Gestionnaire IDTree uniquement)
                  </u>
                  <Space />
                </div>
              )}
            </>
          )}
        </PageLoader>
      </NavFocus>
      {isOwlShown && !currentModal && (
        <>
          {showOwlMenu && (
            <div style={{ position: "fixed", right: 24, bottom: 100, zIndex: 41 }}>
              <ContextMenu
                className="!p-0"
                positionRight
                positionBottom
                width={520}
                maxHeight={window.innerHeight - 140}
                onClose={() => setShowOwlMenu(false)}>
                <div className="text-sm font-bold" style={{ padding: "12px 16px" }}>
                  {visibleIssues.length > 0
                    ? t("project_review_editor_title", { count: visibleIssues.length, s: visibleIssues.length > 1 ? "s" : "" })
                    : t("project_review_editor_ok")}
                </div>
                <div style={{ padding: "0 16px" }}>
                  {getOwlCards().map((card, i) =>
                    card.issues.length > 1 ? (
                      <ReviewMessageGroup
                        key={card.key}
                        kind="error"
                        text={getGroupText(card.issues[0])}
                        items={card.issues.map((issue) => ({ label: issue.group || issue.label, onClick: () => goToIssue(issue.id) }))}
                      />
                    ) : (
                      card.issues.map((issue) => (
                        <ReviewMessage
                          key={issue.id}
                          index={i}
                          kind={issue.severity}
                          //The lines under the title already say what the sentence says : only the title is kept above them
                          text={issue.details?.length ? issue.message.split(" : ")[0] : issue.message}
                          details={issue.details}
                          isAi={issue.kind === "reverse-suspect"}
                          onClick={() => goToIssue(issue.id)}
                          action={
                            issue.severity === "warning" ? (
                              <span className="flex" style={{ gap: 8 }} onClick={(event) => event.stopPropagation()}>
                                {issue.kind === "reverse-suspect" ? (
                                  <>
                                    {!issue.needsConfirmation && (
                                      <AiActionButton
                                        text={t(issue.suggestedReverse ? "template_issue_reverse_apply" : "template_issue_reverse_unapply")}
                                        onClick={() => applyReverse(issue)}
                                      />
                                    )}
                                    <AiActionButton isMuted icon={faTimes} text={t("utils_ignore")} onClick={() => dismissIssue(issue.id)} />
                                  </>
                                ) : (
                                  <ListButton icon={faTimes} isSmall text={t("utils_ignore")} tooltipPosition="left" onClick={() => dismissIssue(issue.id)} />
                                )}
                              </span>
                            ) : undefined
                          }
                        />
                      ))
                    )
                  )}
                </div>
                {/* Footer as the one of the modals : the way back, and the main action when there are edits to save (each takes half of the width) */}
                {(cameFromProject || props.topic.isEdited) && (
                  <div className="flex" style={{ alignItems: "stretch", gap: 12, padding: "10px 16px 14px", borderTop: "1px solid rgba(0, 0, 0, 0.08)", position: "sticky", bottom: 0, background: "white" }}>
                    {cameFromProject && (
                      <div className="flex1 [&>button]:!w-full">
                        <Button
                          isFullWidth
                          isWithBorder
                          onClick={() => {
                            setShowOwlMenu(false)
                            if (props.topic.isEdited && PREVENT_LEAVING_UNSAVED) setCurrentModal(MODAL_LEAVING_UNSAVED)
                            else back()
                          }}>
                          <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} />
                          {t("project_review_editor_back")}
                        </Button>
                      </div>
                    )}
                    {props.topic.isEdited && (
                      <div className="flex1 [&>button]:!w-full">
                        <Button
                          isFullWidth
                          className="primary"
                          onClick={() => {
                            setShowOwlMenu(false)
                            save()
                          }}>
                          <FontAwesomeIcon icon={faSave} style={{ marginRight: 8 }} />
                          {t("utils_save")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </ContextMenu>
            </div>
          )}
          <div style={{ display: "contents" }} onPointerDownCapture={() => (wasOwlMenuOpenRef.current = showOwlMenu)}>
            <ProjectReviewLauncher
              label={t("project_review_launcher_editor")}
              errorsCount={issuesErrorsCount}
              warningsCount={visibleIssues.length - issuesErrorsCount}
              onClick={() => setShowOwlMenu(!wasOwlMenuOpenRef.current)}
            />
          </div>
        </>
      )}
    </TemplateIssuesContext.Provider>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  axis: state.axis,
  template: state.template,
  topic: state.topic,
  project: state.project
})

export default connect(mapStateToProps)(withTranslation()(TemplateEditorRoute))
